/**
 * API: Klaviyo
 * -----------------------------------------------------------------------------
 * Provides a wrapper for the Klaviyo API.
 * - https://developers.klaviyo.com/en/reference/api-overview
 *
 * Payload must match the structure outlined in the link below:
 * - https://developers.klaviyo.com/en/reference/create_client_subscription
 */

/**
 * Register user to Klaviyo's Back in Stock flow.
 * @param {Object} data - Form data object formatted to include type, attributes
 * and relationships.
 */
export function registerBackInStock(data = {}) {
  const key = cnvs?.credentials?.klaviyo?.publicKey

  if (!data || !key) {
    return Promise.reject(
      new Error('Email address or Klaviyo credentials not set.'),
    )
  }

  const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${key}`
  const payload = {

    /**
     * Must use JSON.stringify() because of strict Klaviyo requirements.
     */
    body: JSON.stringify({ data }),
    headers: {
      accept: 'application/json',
      revision: '2023-12-15',
      'content-type': 'application/json',
    },
    method: 'POST',
  }

  return new Promise((resolve, reject) => {
    fetch(
      url,
      payload,
    )
      .then(response => resolve(response))
      .catch(
        error => {
          reportError('Klaviyo API request failed', error)
          reject(error)
        },
      )
  })
}

/**
 * Subscribes a new email address to Klaviyo.
 * - {data} can only include the properties `type` and `attributes`.
 *
 * @param {Object} data - Form data object.
 * @param {Boolean} [isBackInStock] - If subscription is a back in stock event.
 * @param {Number} [variantId] - Required for back in stock event.
 * @returns {Promise}
 */
export default function subscribe(data = {}) {
  const key = cnvs?.credentials?.klaviyo?.publicKey

  if (!data || !key) {
    return Promise.reject(
      new Error('Email address or Klaviyo credentials not set.'),
    )
  }

  const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${key}`
  const payload = {

    /**
     * Must use JSON.stringify() because of strict Klaviyo requirements.
     */
    body: JSON.stringify({ data }),
    headers: {
      revision: '2023-12-15',
      'content-type': 'application/json',
    },
    method: 'POST',
  }

  return new Promise((resolve, reject) => {
    fetch(
      url,
      payload,
    )

      /**
       * Using response.text() as response.json() resulted in a SyntaxError due
       * to 'Unexpected end of JSON'.
       */
      .then(response => response.text())
      .then(response => resolve(response))
      .catch(
        error => {
          reportError('Klaviyo API request failed', error)
          reject(error)
        },
      )
  })
}
